/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Component, Inject, Input, NgZone, OnInit} from '@angular/core';
import {NgForm} from '@angular/forms';
import {Subject} from 'rxjs';
import * as _ from 'lodash';
import {BsModalRef} from 'ngx-bootstrap/modal';
import template from './segment-trigger-modal.component.html';
import {VisitorProfileFields} from '../../components/api/resources/resource/visitor-profile-fields';
import {TriggerFormLogic} from '../trigger/trigger-form-logic';
import {Uuid} from '../../components/util/uuid';
import {AppEventBusService} from '../common/events/app-event-bus.service';
import {
  clearFormValidation,
  createFormSubmitState,
  exposeFormSubmitStateAccessors,
  FormControlError,
  FormSubmitState,
  handleApiValidationErrors,
  markFormSubmitError,
  markFormSubmitSuccess,
  updateFormWarnings
} from '../common/controller/form-submit-state';
import {FolderResource} from '../../components/api/resources/resource/folder.resource';
import {TriggerResource} from '../../components/api/resources/resource/trigger.resource';
import {AdnTimeShifterService} from '../../components/util/adn-time-shifter.service';
import {LocalNetworkProfile} from '../../components/session/local-network-profile';
import {LocationResource} from '../../components/api/resources/resource/location';
import {SearchResource} from '../../components/api/resources/resource/search';

@Component({
  standalone: false,
  selector: 'adn-segment-trigger-modal',
  template
})
export class SegmentTriggerModalComponent implements OnInit {
  @Input() segmentModel: any;
  @Input() entry: any;

  readonly onClosed = new Subject<void>();

  apiModel: any;
  editingModel: any = {};
  formLogic: TriggerFormLogic;
  submitState: FormSubmitState = createFormSubmitState();
  pending = false;

  private readonly formControlErrors: FormControlError[] = [];

  constructor(
    public bsModalRef: BsModalRef,
    private readonly eventBus: AppEventBusService,
    @Inject(TriggerResource) private readonly Trigger: any,
    @Inject(AdnTimeShifterService) private readonly adnTimeShifter: any,
    @Inject(FolderResource) private readonly Folder: any,
    @Inject(SearchResource) private readonly searchResource: any,
    @Inject(LocationResource) private readonly locationResource: any,
    @Inject(LocalNetworkProfile) private readonly LocalNetworkProfile: any,
    private readonly visitorProfileFields: VisitorProfileFields,
    private readonly ngZone: NgZone
  ) {
    exposeFormSubmitStateAccessors(this, this.submitState);
  }

  ngOnInit(): void {
    this.apiModel = this.entry || new this.Trigger({
      addUserSegments: [{id: this.segmentModel.id, name: this.segmentModel.name}]
    });
    this.editingModel = _.cloneDeep(this.apiModel);
    this.editingModel.helpfulCriteria = this.editingModel.helpfulCriteria || [];

    this.formLogic = new TriggerFormLogic(this.editingModel, {
      adnTimeShifter: this.adnTimeShifter,
      Folder: this.Folder,
      searchResource: this.searchResource,
      visitorProfileFields: this.visitorProfileFields,
      locationResource: this.locationResource
    });
  }

  markDirty(editForm: NgForm): void {
    editForm.control.markAsDirty();
  }

  submit(editForm: NgForm): void {
    if (this.pending) {
      return;
    }

    this.pending = true;
    this.submitState.ngForm = editForm;
    clearFormValidation(this.submitState, this.formControlErrors);

    this.formLogic.onBeforeSubmit();

    if (!this.editingModel.id) {
      this.editingModel.id = Uuid.generate();
    }

    _.forEach(this.editingModel, (val, key) => {
      if (key.charAt(0) !== '$' && !_.isFunction(val)) {
        this.apiModel[key] = val;
      }
    });

    const saveTarget = _.isFunction(this.apiModel.savePartial) ? this.apiModel : this.editingModel;

    saveTarget.savePartial(null, 'save', null).then((apiResource: any) => {
      this.ngZone.run(() => {
        updateFormWarnings(this.submitState, apiResource || this.editingModel, this.LocalNetworkProfile.isPureMarketplacePlatform());
        if (apiResource) {
          _.merge(this.editingModel, apiResource);
          Object.assign(this.apiModel, apiResource);
        }
        markFormSubmitSuccess(this.submitState, editForm);
        this.bsModalRef.hide();
        this.onClosed.next();
      });
    }, (response: any) => {
      if ((response.status === 400 || response.status === 403 || response.status === 422)
        && (response.data.type === 'ValidationResult' || response.data.type === 'ErrorMessage')) {
        handleApiValidationErrors(
          this.submitState,
          this.editingModel,
          _.get(response, ['data', 'errors']) || response.data,
          this.formControlErrors,
          editForm
        );
      }
      markFormSubmitError(this.submitState, editForm);
    }).finally(() => {
      this.pending = false;
      this.eventBus.emit('SUBMIT_PROCESSED');
    });
  }

  dismiss(): void {
    this.bsModalRef.hide();
    this.onClosed.next();
  }
}
